import type { EdgeBar } from '../types/chart'
import type { PeriodKey } from '../utils/resolution'

/**
 * Per-(ticker, period) cache for the always-loaded daily/weekly/monthly
 * preload (Task 3 of the chart-lookback spec).
 *
 * Spec: docs/superpowers/specs/2026-05-06-chart-multi-period-preload-design.md
 *
 * Promise-first: an entry exists from the moment a fetch starts (`set` is
 * called with the in-flight promise), so a `setPeriod` racing the prefetcher
 * shares the in-flight promise instead of issuing a duplicate request.
 *
 * LRU on ticker (cap = MAX_TICKERS). All of an evicted ticker's period entries
 * go together — there is no scenario where keeping `1W` but not `1D` for a
 * ticker is useful. 15-min staleness via `isStale()`; the caller is
 * responsible for issuing a revalidation fetch on a stale hit (SWR pattern).
 */

type CacheKey = string

interface CacheEntry {
  promise: Promise<EdgeBar[]>
  /**
   * Abort handle for the underlying fetch. Calling `controller.abort()`
   * cancels the in-flight network request (when fetch sees the signal) and
   * causes `promise` to reject with `AbortError`. `null` when the caller
   * didn't supply a controller (e.g. a synthetic test promise).
   */
  controller: AbortController | null
  bars: EdgeBar[] | null
  fetchedAt: number
  ticker: string
  periodKey: PeriodKey
  adjusted: boolean
}

/**
 * Mode dimensions that affect the bar VALUES the backend returns. Cache keys
 * must encode every mode that produces a different OHLCV — otherwise a toggle
 * silently serves stale-mode bars. `extendedHours` is intentionally omitted:
 *   - intraday URLs include `&market_session=mkt`, so the URL-keyed
 *     EdgeDataFeed.cache already separates them at the network layer
 *   - D/W/M URLs never carry `market_session` (intraday-only param) so the
 *     bar values are identical in both extendedHours modes for daily+
 *   - the post-fetch client-side regular-session filter is intraday-only
 *     after the daily-bar fix in DataLayer.applyBarFilter
 */
export interface CacheModeOpts {
  /** Defaults to true (matches backend default of split-adjusted prices). */
  adjusted?: boolean
}

function key(ticker: string, periodKey: PeriodKey, opts: CacheModeOpts = {}): CacheKey {
  const adjusted = opts.adjusted ?? true
  return `${ticker}#${periodKey}#adj-${adjusted ? '1' : '0'}`
}

export class MultiPeriodCache {
  static readonly MAX_TICKERS = 3
  /**
   * Stale TTL — entries older than this should be revalidated on read. 1 hour
   * is the right balance for a memory-only cache: long enough that toggling
   * 1D/1W/1M repeatedly within a session doesn't refetch, short enough that
   * the today-bar (intra-day price drift on D/W/M) is reasonably fresh.
   */
  static readonly STALE_AFTER_MS = 60 * 60 * 1000

  private entries = new Map<CacheKey, CacheEntry>()
  /** LRU bookkeeping: front (index 0) = most recently used, back = oldest. */
  private tickerOrder: string[] = []
  private readonly maxTickers: number

  /**
   * @param maxTickers LRU cap on distinct tickers. Defaults to `MAX_TICKERS`
   *   (3), sized for a SINGLE chart. A SHARED cache — one per datafeed, spanning
   *   an N×M grid of distinct-ticker cells — MUST pass a larger cap, or every
   *   cell past the 3rd distinct ticker evicts a sibling's warm entry and aborts
   *   its in-flight prefetch (thrashing the very cache meant to help).
   */
  constructor(maxTickers: number = MultiPeriodCache.MAX_TICKERS) {
    this.maxTickers = maxTickers
  }

  get(ticker: string, periodKey: PeriodKey, opts: CacheModeOpts = {}): CacheEntry | null {
    const entry = this.entries.get(key(ticker, periodKey, opts))
    if (!entry) return null
    this.touchTicker(ticker)
    return entry
  }

  /**
   * Insert a new in-flight entry. Pass the matching `AbortController` so the
   * cache can cancel the fetch on LRU eviction or `evictTicker()`.
   *
   * If an entry already exists at `(ticker, periodKey)`, its controller is
   * aborted before the replacement is installed. This is correct for both
   * post-resolve overwrites (no-op) and SWR revalidation while the old
   * fetch is still in-flight (kill the stale one; the fresh one will populate).
   */
  set(
    ticker: string,
    periodKey: PeriodKey,
    promise: Promise<EdgeBar[]>,
    controller: AbortController | null = null,
    opts: CacheModeOpts = {},
  ): void {
    const k = key(ticker, periodKey, opts)
    const previous = this.entries.get(k)
    if (previous?.controller) previous.controller.abort()

    const entry: CacheEntry = {
      promise,
      controller,
      bars: null,
      fetchedAt: Date.now(),
      ticker,
      periodKey,
      adjusted: opts.adjusted ?? true,
    }
    promise
      .then((bars) => {
        // Only record if this is still the active entry for the key — a later
        // `set()` (e.g. SWR revalidation) will have replaced it, and we don't
        // want a stale resolution to clobber the newer one's bars.
        if (this.entries.get(k) === entry) {
          entry.bars = bars
        }
      })
      .catch(() => {
        // Swallow — consumers `await entry.promise` directly and handle their
        // own rejection (including AbortError on eviction). We just don't
        // want an unhandled-rejection warning from this bookkeeping `.then`.
      })

    this.entries.set(k, entry)
    this.touchTicker(ticker)
    this.evictIfOverCap()
  }

  isStale(ticker: string, periodKey: PeriodKey, opts: CacheModeOpts = {}): boolean {
    const entry = this.entries.get(key(ticker, periodKey, opts))
    if (!entry) return false
    return Date.now() - entry.fetchedAt >= MultiPeriodCache.STALE_AFTER_MS
  }

  /**
   * Drop all entries for a ticker, aborting any in-flight fetches. Use this
   * when the user explicitly leaves a chart for a ticker that won't be coming
   * back in this session (e.g. on chart destroy with a refcount reaching zero).
   */
  evictTicker(ticker: string): void {
    for (const k of [...this.entries.keys()]) {
      const entry = this.entries.get(k)
      if (entry?.ticker === ticker) {
        entry.controller?.abort()
        this.entries.delete(k)
      }
    }
    this.tickerOrder = this.tickerOrder.filter((t) => t !== ticker)
  }

  /** Move `ticker` to the front of the LRU order. */
  private touchTicker(ticker: string): void {
    const idx = this.tickerOrder.indexOf(ticker)
    if (idx >= 0) this.tickerOrder.splice(idx, 1)
    this.tickerOrder.unshift(ticker)
  }

  private evictIfOverCap(): void {
    while (this.tickerOrder.length > this.maxTickers) {
      const oldest = this.tickerOrder.pop()
      if (oldest == null) break
      for (const k of [...this.entries.keys()]) {
        const entry = this.entries.get(k)
        if (entry?.ticker === oldest) {
          entry.controller?.abort()
          this.entries.delete(k)
        }
      }
    }
  }
}
